import React from "react"
import Link from "next/link"
import classNames from "classnames"
import { Mutation } from "react-apollo"

import { LOCAL_TOGGLE_FOUNDER_ASSIST } from "../../apollo-state/graphql/mutations"
import {
  nextRefreshText,
  PREMIUM_REFRESHES_PER_DAY,
  profileChangeText,
  refreshAvailable,
} from "../../lib/founder-refresh"

import "./founder-progress-ribbon.scss"

const DAY_MS = 24 * 60 * 60 * 1000
const QUARTER_DAYS = 90

// Monday 00:00 local time of the current week
const startOfWeek = (now = new Date()) => {
  const d = new Date(now)
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7))
  return d.getTime()
}

// Progress this week, in one line: actions done, the check-in streak, the
// next review, and the plan refresh. Replaces the stat-tile row on the dashboard.
export const weekProgress = (actions, now = new Date()) => {
  const since = startOfWeek(now)
  const doneThisWeek = (actions || []).filter(
    (a) => a.status === "archived" && a.completedAt && new Date(a.completedAt).getTime() >= since
  ).length
  const open = (actions || []).filter((a) => a.status === "open").length
  return { doneThisWeek, total: doneThisWeek + open }
}

const FounderProgressRibbon = ({ fa, isPremium }) => {
  if (!fa) return null
  const { doneThisWeek, total } = weekProgress(fa.actions)
  const pattern = Array.isArray(fa.weeklyCheckinPattern)
    ? fa.weeklyCheckinPattern
    : [false, false, false, false, false]
  const checkins = pattern.filter(Boolean).length
  const daysIntoQuarter = Math.min(QUARTER_DAYS, Math.max(0, Number(fa.daysIntoQuarter) || 0))
  const daysToReview = QUARTER_DAYS - daysIntoQuarter
  const sinceCheckin = fa.lastCheckinAt
    ? Math.floor((Date.now() - new Date(fa.lastCheckinAt).getTime()) / DAY_MS)
    : null
  const refresh = fa.refresh
  const canRefresh = refreshAvailable(refresh)
  const changed = profileChangeText(fa)
  const refreshDue = canRefresh && (Boolean(changed) || sinceCheckin === null || sinceCheckin >= 5)

  // Premium: 3 a day. Free: 1 a week (backend utils/founder-entitlements.js)
  let note = null
  if (changed) {
    note = canRefresh
      ? `${changed} Refresh to bring it up to date.`
      : `${changed} Your next refresh, ${nextRefreshText(refresh.nextRefreshAt)}, will catch it up.`
  } else if (!canRefresh) {
    note = `Next refresh ${nextRefreshText(refresh.nextRefreshAt)}`
  } else if (isPremium && refresh && refresh.canRefresh && refresh.left < refresh.limit) {
    note = `${refresh.left} of ${refresh.limit} left today`
  }

  return (
    <div className="progress-ribbon">
      <p className="progress-ribbon__item">
        <strong>
          {total > 0 ? `${doneThisWeek} of ${total}` : "No actions"}
        </strong>{" "}
        {total > 0 ? "done this week" : "yet this week"}
      </p>
      <p className="progress-ribbon__item">
        <span className="progress-ribbon__dots" aria-hidden="true">
          {pattern.map((on, i) => (
            <span key={i} className={classNames("progress-ribbon__dot", { "progress-ribbon__dot--on": on })} />
          ))}
        </span>
        <span>
          Checked in <strong>{checkins} of the last 5 weeks</strong>
        </span>
      </p>
      <p className="progress-ribbon__item">
        {fa.needsQuarterlyReview ? (
          <strong>Your 90-day review is open</strong>
        ) : (
          <span>
            Next review in <strong>{daysToReview} days</strong>
          </span>
        )}
      </p>
      <div className="progress-ribbon__action">
        <Mutation mutation={LOCAL_TOGGLE_FOUNDER_ASSIST}>
          {(toggleRefresh) => (
            <button
              type="button"
              className={classNames("progress-ribbon__checkin", {
                "progress-ribbon__checkin--due": refreshDue,
              })}
              disabled={!canRefresh}
              onClick={() => toggleRefresh({ variables: { step: "q1" } })}
            >
              Refresh plan
            </button>
          )}
        </Mutation>
        {(note || !isPremium) && (
          <p className="progress-ribbon__note">
            {note}
            {note && !isPremium && " · "}
            {!isPremium && (
              <Link href="/pricing">
                <a className="progress-ribbon__upsell">
                  Premium refreshes {PREMIUM_REFRESHES_PER_DAY} times a day
                </a>
              </Link>
            )}
          </p>
        )}
      </div>
    </div>
  )
}

export default FounderProgressRibbon
